/**
 * ============================================================
 * ELCHME SSO INTEGRATION FOR BELONGINGSESSIONS.COM
 * Secure token-based SSO with SHA-256 verification
 * ============================================================
 */

const ELCHME_BASE_URL = "https://elchme.com";
const SSO_SHARED_SECRET = process.env.REACT_APP_SSO_SECRET_ELCHME || "belonging_sessions_shared_secret_2024";

/**
 * Generate SSO link for ElchmE Research Portal
 * @param {Object} user - User object with id, name, email, and optional roles
 * @returns {Promise<string>} - SSO URL to redirect user to
 */
export async function generateElchmEResearchLink(user) {
  // 1. Prepare user data
  const userData = {
    id: user.id || user._id || "unknown",
    name: user.name || `${user.first_name} ${user.last_name}` || "User",
    email: user.email || "",
    roles: user.roles || ["user"]
  };
  
  // 2. Encode user data as base64
  const bs_user = btoa(JSON.stringify(userData));
  
  // 3. Create timestamp (milliseconds)
  const bs_time = Date.now().toString();
  
  // 4. Generate verification token
  const bs_token = await generateSSOToken(bs_user, bs_time);
  
  // 5. Build and return URL
  return `${ELCHME_BASE_URL}/research?bs_user=${encodeURIComponent(bs_user)}&bs_token=${bs_token}&bs_time=${bs_time}`;
}

/**
 * Generate SHA-256 verification token
 * Uses Web Crypto API (works in all modern browsers)
 */
async function generateSSOToken(userData, timestamp) {
  const data = `${userData}:${timestamp}:${SSO_SHARED_SECRET}`;
  
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);
  const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  
  return hashHex.substring(0, 32);
}
